import classNames from 'classnames';
import { cloneDeep } from 'lodash';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Switch } from 'antd';
import { Scrollbars, SliderInput } from '@meitu/saas-ui';
import { AppTheme } from '@meitu/saas-ui/es/theme';
import { CustomTitle } from '@meitu/saas-ui/es/title';
import {
    getShapeTypeConfig,
    type IEdgelessTextLayer,
    type ShapeTextProps,
    type TextLayer,
} from '@meitu/saas-editor-core/es/layers/text-layer';
import { type TextEditorPlugin } from '@meitu/saas-editor-core/es/plugins/text-editor';
import { imageConverter } from '@meitu/saas-editor-core/es/utils/file-kit';
import { mapRange } from '@meitu/saas-editor-core/es/utils/math-kit';
import IconFont from '@meitu/xdesign-icon-font';
import LabelAction from '@/components/label-action';
import { useEditor } from '@/hooks/use-editor';
import { useLocaleStore } from '@/i18n';
import DragPopover from '../../components/drag-popover';
import { useEffectiveShapeExtension } from '../../hooks';
import { type ShapeTextMaterialRaw, type WidgetCommonProps } from '../../types';
import { Tracker } from '../../utils';
import './style.less';

type Props = {
    shapeTextList: ShapeTextMaterialRaw[];
    onClose: () => void;
} & WidgetCommonProps;

export const SHAPE_TEXT_POPOVER = 'shape-text-popover';
export const ShapeTextPanel: React.FC<Props> = ({ shapeTextList, onClose, theme, isIntl, layerId, recordId }) => {
    const { $t } = useLocaleStore();

    const editor = useEditor();

    // 插入操作组件的索引
    const [insertIndex, setInsertIndex] = useState<number>(-1);

    const draggleRef = useRef<HTMLDivElement>(null);
    const scrollbarsRef = useRef<any>(null);
    const pendingAdjustmentRef = useRef<{
        props: ShapeTextProps;
        name: string;
        value: number | boolean;
    } | null>(null);

    const shapeExtension = useEffectiveShapeExtension(layerId);

    const shapeType = shapeExtension ? shapeExtension.curveTextType : null;

    const [shapeTextProps, setShapeTextProps] = useState<ShapeTextProps[] | null>(
        shapeType ? cloneDeep(getShapeTypeConfig()[shapeType]) : null,
    );

    const finalizePendingAdjustment = useCallback(async () => {
        const pending = pendingAdjustmentRef.current;
        if (!pending) return;
        pendingAdjustmentRef.current = null;
        await editor.getInstance<TextLayer>('TextLayer').adjustShapeTextProps(layerId, pending.name, pending.value, {
            props: pending.props,
            writeHistory: true,
        });
    }, [editor, layerId]);

    useEffect(() => {
        const host = editor.services.edgelessCanvas.getByLayerId(layerId) as IEdgelessTextLayer | null;
        void host?.startShapeTextGLRender().catch((error) => {
            console.error('startShapeTextGLRender error:', error);
        });

        return () => {
            void finalizePendingAdjustment()
                .catch((error) => console.error('finalize Shape Text adjustment error:', error))
                .then(() => host?.finishShapeTextGLRender())
                .catch((error) => console.error('finishShapeTextGLRender error:', error));
        };
    }, [editor, finalizePendingAdjustment, layerId]);

    const cancelShapeText = async () => {
        pendingAdjustmentRef.current = null;
        const nextLayer = await editor.getInstance<TextLayer>('TextLayer').cancelShapeEffect(layerId);
        if (!nextLayer) return;

        setInsertIndex(-1);
        setShapeTextProps(null);

        Tracker.cancelShapeText({
            recordId,
            stageType: editor.services.editor.getCanvasType(),
        });
    };

    const applyShapeText = async (data: ShapeTextMaterialRaw) => {
        if (shapeType === data['shape_type']) await finalizePendingAdjustment();
        else pendingAdjustmentRef.current = null;
        const editingLayer = editor.getInstance<TextEditorPlugin>('TextEditorPlugin').getEditingLayer();
        if (editingLayer) {
            await editor.getInstance<TextEditorPlugin>('TextEditorPlugin').exit();
        }

        const nextShapeType = data['shape_type'];
        const materialID = data['id'];

        void editor
            .getInstance<TextLayer>('TextLayer')
            .applyShapeText(layerId, nextShapeType, { materialID })
            .catch((error) => console.error('applyShapeText error:', error));
        setShapeTextProps(cloneDeep(getShapeTypeConfig()[nextShapeType]));

        // eslint-disable-next-line react-hooks/rules-of-hooks
        Tracker.useShapeText({
            recordId,
            stageType: editor.services.editor.getCanvasType(),
            shapeType: nextShapeType,
            materialID,
        });
    };

    const adjustShapeTextProps = async (
        props: ShapeTextProps,
        name: string,
        value: number | boolean,
        options = { writeHistory: true },
    ) => {
        if (!shapeTextProps) return;

        const nextProps = shapeTextProps.map((item) => {
            if (item.param === name) {
                return {
                    ...item,
                    value,
                };
            }
            return item;
        }) as ShapeTextProps[];

        setShapeTextProps(nextProps);

        const { writeHistory } = options;
        pendingAdjustmentRef.current = writeHistory ? null : { props, name, value };
        void editor
            .getInstance<TextLayer>('TextLayer')
            .adjustShapeTextProps(layerId, name, value, { writeHistory, props })
            .catch((error) => console.error('adjustShapeTextProps error:', error));

        if (writeHistory) {
            Tracker.adjustShapeTextProps({
                recordId,
                stageType: editor.services.editor.getCanvasType(),
            });
        }
    };

    useEffect(() => {
        shapeTextList.forEach((v) => {
            window._stat_track('tpbj_material_show', {
                type_id: 1088,
                material_id: v.id,
            });
        });
    }, [shapeTextList]);

    useEffect(() => {
        setShapeTextProps(shapeType ? cloneDeep(getShapeTypeConfig()[shapeType]) : null);
    }, [shapeType]);

    useEffect(() => {
        if (!shapeExtension) return;
        const index = shapeTextList.findIndex((item) => item['shape_type'] === shapeExtension.curveTextType);
        if (index === -1) return;
        const activeIndex = index + 1; //+1 是因为第一个元素是自定义的重置元素
        const insertIndex = Math.floor(activeIndex / 3) * 3 + 2;
        const lastElIndex = shapeTextList.length;
        setInsertIndex(insertIndex >= lastElIndex ? lastElIndex : insertIndex); // 设置插入操作组件的索引

        const activeEl = document.querySelector(`.shape-text-panel__item:nth-of-type(${activeIndex})`) as HTMLElement;
        if (activeEl) requestAnimationFrame(() => scrollbarsRef.current?.scrollTop(activeEl.offsetTop - 16)); // 减去16px，是因为操作组件的padding-top为16px
    }, [shapeExtension?.curveTextType]);

    //  渲染操作组件
    const renderActions = () => {
        return shapeTextProps && shapeExtension ? (
            <div
                className={classNames('shape-text-panel__actions', {
                    'shape-text-panel__actions--intl': isIntl,
                })}
            >
                {shapeTextProps.map((item, idx) => {
                    if ('isBool' in item) {
                        return (
                            <LabelAction key={idx} title={item.label} align>
                                <div className="shape-text-panel__control">
                                    <Switch
                                        checked={item.value}
                                        onChange={() => adjustShapeTextProps(item, item.param, !item.value)}
                                    />
                                </div>
                            </LabelAction>
                        );
                    }

                    return (
                        <LabelAction key={item.label} title={item.label} align>
                            <div className="shape-text-panel__control">
                                <SliderInput
                                    type="horizontal"
                                    showStepBtn
                                    min={item.range[0]}
                                    max={item.range[1]}
                                    precision={1}
                                    value={mapRange(shapeExtension[item.param], item.ratio, item.range)}
                                    onChange={(v) => adjustShapeTextProps(item, item.param, v, { writeHistory: false })}
                                    onAfterChange={(v) =>
                                        adjustShapeTextProps(item, item.param, v, { writeHistory: true })
                                    }
                                />
                            </div>
                        </LabelAction>
                    );
                })}
            </div>
        ) : null;
    };
    return (
        <DragPopover draggleRef={draggleRef} innerDoms={['shape-text-panel__control']}>
            <div className="shape-text-panel" ref={draggleRef}>
                <div className="shape-text-panel__title">
                    <CustomTitle
                        leftNode={<div className="left">{$t((v) => v.panel.textPanel.transform)}</div>}
                        rightNode={
                            <div className="right" onClick={onClose}>
                                <IconFont name="CrossBlack" fontSize={12} color="var(--content-system-secondary)" />
                            </div>
                        }
                    />
                </div>
                {/* safari低版本需要包一层div 滚动列表才能撑开*/}
                <div style={{ flex: 1 }}>
                    <Scrollbars autoHide scrollbarsRef={scrollbarsRef}>
                        <div className="shape-text-panel__list">
                            <div className="shape-text-panel__item" onClick={cancelShapeText}>
                                <IconFont name="SlashCircle" fontSize={38} />
                            </div>

                            {shapeTextList.map((item, index) => {
                                const thumbnailKey =
                                    `thumbnail${theme === AppTheme.white ? '' : '_black'}` as keyof ShapeTextMaterialRaw;
                                const thumbnail =
                                    (item[thumbnailKey] as ShapeTextMaterialRaw['thumbnail'])?.url ||
                                    item.thumbnail.url;

                                const isActive = shapeExtension
                                    ? shapeExtension.curveTextType === item['shape_type']
                                    : false;

                                return (
                                    <React.Fragment key={item.id}>
                                        <div
                                            data-curve-type={item['shape_type']}
                                            className={classNames('shape-text-panel__item', {
                                                'shape-text-panel__item--active': isActive,
                                            })}
                                            onClick={() => applyShapeText(item)}
                                        >
                                            <img src={imageConverter(thumbnail, 140)} alt="" />
                                        </div>
                                        {/* 插入操作组件 */}
                                        {insertIndex - 1 === index && <p>{renderActions()}</p>}
                                    </React.Fragment>
                                );
                            })}
                        </div>
                    </Scrollbars>
                </div>
            </div>
        </DragPopover>
    );
};
